/** Mature woodland silhouettes, authored once into the shared 128px scenery cache. */
import type { Graphics } from 'pixi.js';
import { mix, shade } from '../data/palette.ts';
import type { NodeStageId } from '../sim/systems/nodes.ts';

type Paint = (points: number[], color: number, alpha?: number) => void;

/** A branch-sized leaf mass: irregular shoulders, a broad lit crown and hanging foliage. */
function bough(paint: Paint, cx: number, cy: number, rx: number, ry: number, tint: number, lean = 0): void {
  const p = (points: number[]) => points.map((n, i) => i % 2 === 0 ? cx + (n + lean) * rx : cy + n * ry);
  // The notches are leaf-bearing branch tips, rather than a regular polygon around a centre.
  paint(p([-1, .08, -.93, -.39, -.65, -.55, -.53, -.88, -.16, -.84, .11, -1,
    .45, -.74, .75, -.71, .79, -.38, 1, -.14, .92, .31, .71, .39,
    .59, .7, .28, .61, .02, .84, -.26, .63, -.65, .67, -.69, .41, -.91, .39]), shade(tint, .62));
  paint(p([-1, .08, -.93, -.39, -.65, -.55, -.53, -.88, -.16, -.84,
    -.32, -.18, -.09, .13, -.54, .29]), shade(tint, 1.08));
  paint(p([-.53, -.88, -.16, -.84, .11, -1, .45, -.74, .75, -.71,
    .5, -.24, .09, -.36, -.32, -.18]), mix(tint, 0xc2c693, .25));
  paint(p([.75, -.71, .79, -.38, 1, -.14, .92, .31, .51, .17,
    .24, -.02, .5, -.24]), shade(tint, .84));
  paint(p([-.54, .29, -.32, -.18, .09, -.36, .24, -.02, .06, .44,
    -.26, .63, -.65, .67, -.69, .41]), shade(tint, .91));
  paint(p([.24, -.02, .51, .17, .92, .31, .71, .39, .59, .7,
    .28, .61, .02, .84, .06, .44]), shade(tint, .7));
  // Small broken planes articulate the foliage without a mesh of high-contrast seams.
  paint(p([-.85, -.25, -.65, -.55, -.45, -.38, -.58, -.02]), mix(tint, 0xb7be89, .18));
  paint(p([-.12, -.65, .11, -1, .26, -.78, .19, -.52]), mix(tint, 0xd0ca95, .21));
}

function pine(paint: Paint, worked: boolean): void {
  const tint = 0x4b6550;
  // A single ragged canopy connects the hanging branches. Its broad shoulders are
  // deliberately staggered, so neighbouring pines never read as stacked triangles.
  paint([-4, -108, 3, -93, 12, -85, 8, -81, 24, -72, 18, -68,
    31, -55, 24, -54, 43, -35, 34, -34, 38, -28, 17, -28,
    9, -23, -4, -28, -23, -24, -20, -31, -44, -32, -34, -40,
    -39, -44, -25, -56, -30, -57, -17, -73, -22, -75,
    -9, -89, -13, -90], shade(tint, .63));
  paint([-4, -108, -6, -85, -22, -75, -9, -77, -17, -62,
    -2, -67, 8, -81, 3, -93], mix(tint, 0xb7bd8b, .25));
  paint([-4, -108, 3, -93, 12, -85, 8, -81, 24, -72, 18, -68,
    5, -71, -2, -67, -6, -85], shade(tint, .92));
  paint([-17, -73, -30, -57, -25, -56, -39, -44, -16, -48,
    -3, -62, -9, -77], shade(tint, 1.14));
  paint([-9, -77, 5, -71, 18, -68, 31, -55, 24, -54,
    9, -48, -16, -48, -3, -62], shade(tint, .87));
  if (!worked) {
    paint([-25, -56, -34, -40, -44, -32, -20, -31, -23, -24,
      -9, -29, 3, -43, -4, -52], shade(tint, 1.04));
    paint([-4, -52, 9, -48, 24, -54, 43, -35, 34, -34, 38, -28,
      17, -28, 9, -23, -9, -29, 3, -43], shade(tint, .77));
    paint([-33, -38, -21, -45, -4, -52, -14, -38, -25, -33], mix(tint, 0xb6bb85, .22));
    paint([9, -48, 24, -54, 30, -45, 14, -40], shade(tint, 1.05));
  }
  paint([-17, -65, -9, -77, -3, -71, -9, -59], mix(tint, 0xc5c792, .2));
}

/** Draw at the trunk's ground anchor. No randomness, filters or per-frame textures. */
export function drawTree(g: Graphics, x: number, y: number, scale: number, variant: number, stage: NodeStageId): void {
  // Scatter sprites share this authoring canvas, with trunks close to its edges.
  // Compress only their lateral spread; node trees at x64 keep their full crown.
  const sx = Math.max(0, Math.min(scale, (x - 2) / 51, (126 - x) / 51));
  const p = (points: number[]) => points.map((n, i) => i % 2 === 0 ? x + n * sx : y + n * scale);
  const paint: Paint = (points, color, alpha = 1) => { g.poly(p(points)).fill({ color, alpha }); };
  const kind = ((variant % 3) + 3) % 3;
  const cut = stage === 'spent' || stage === 'gone', worked = stage === 'worked';
  const bark = kind === 2 ? 0xaaa68c : 0x92724e;
  paint([-32, 1, -13, -7, 16, -8, 42, 1, 49, 7, 25, 12, -10, 10]
    .map((n, i) => cut && i % 2 === 0 ? n * .55 : n), 0x16251d, cut ? .11 : .25);
  paint([-16, -1, -3, -5, 17, -2, 23, 5, 6, 8, -19, 5], 0x7f7750, .24);
  paint([-5, -10, -16, 3, -7, 2, 0, -4, 11, 5, 17, 4, 6, -10], shade(bark, .75));
  if (cut) {
    paint([-6, 1, -4, -14, 0, -17, 1, -9, 1, 3], bark);
    paint([1, 3, 1, -9, 0, -17, 5, -12, 7, 0], shade(bark, .6));
    paint([-4, -14, 0, -17, 5, -12, 1, -9], 0xd0b78b);
    paint([-2, -13, 0, -14, 3, -12, 1, -11], 0x96784e);
    if (stage === 'spent') {
      paint([9, 2, 31, -3, 38, 2, 16, 7], 0x9b7850);
      paint([16, 7, 38, 2, 38, 7, 16, 12], 0x54432f);
      paint([9, 2, 16, 7, 16, 12, 9, 7], 0xcdb087);
      paint([-24, -1, -12, -7, -7, -4, -19, 3], 0x9a8054);
      paint([-19, 3, -7, -4, -7, 0, -19, 7], 0x67543b);
    }
    return;
  }
  const top = kind === 0 ? -78 : -72;
  paint([-6, 1, -5, -24, -3, -42, -4, top, 0, top - 2, 2, -35, 1, 3], bark);
  paint([1, 3, 2, -35, 0, top - 2, 5, top + 2, 5, -37, 7, -19, 7, 0], shade(bark, .6));
  paint([-4, -2, -2, -25, -3, -36, -1, -28, 0, -6], shade(bark, .72));
  // Forks extend into their own leaf-bearing boughs, rather than stopping beneath
  // a spherical crown. Worked trees expose the cut western scaffold branch.
  if (kind === 0) {
    paint([-1, -32, -19, -38, -16, -35, 1, -27], shade(bark, .9));
    paint([3, -36, 21, -43, 18, -38, 4, -30], shade(bark, .66));
  } else {
    paint([-2, -29, -6, -40, -23, -57, -29, -72, -23, -64, -17, -56, 0, -40], shade(bark, .9));
    paint([3, -31, 8, -47, 26, -65, 30, -82, 31, -69, 25, -58, 8, -40], shade(bark, .66));
    paint([-2, -49, -12, -72, -10, -83, -6, -72, 2, -61], bark);
  }
  if (kind === 0) {
    // Harvesting removes the heavy lower branches, leaving the intact upper crown.
    if (worked) {
      const trimmed: Paint = (points, color, alpha) => {
        paint(points.map((n, i) => i % 2 === 0 ? n * .76 : Math.min(n, -41)), color, alpha);
      };
      pine(trimmed, true);
    } else pine(paint, false);
  } else {
    const tint = kind === 1 ? 0x6d7d4b : 0x718364;
    // Deep continuous lower foliage joins the overlapping broad, flattened boughs.
    if (!worked) paint([-41, -58, -28, -81, 6, -94, 38, -78, 49, -56,
      40, -36, 18, -31, -5, -28, -34, -39, -47, -48], shade(tint, .58));
    bough(paint, kind === 1 ? 13 : 20, -78, 27, kind === 1 ? 23 : 27, shade(tint, .82), -.04);
    if (!worked) bough(paint, -26, kind === 1 ? -60 : -67, 23, 22, tint, -.03);
    bough(paint, kind === 1 ? -9 : -7, kind === 1 ? -86 : -93, 28, kind === 1 ? 21 : 15, shade(tint, 1.05), -.06);
    bough(paint, worked ? 17 : 25, kind === 1 ? -59 : -61, worked ? 23 : 24, 22, shade(tint, .86));
    if (!worked) bough(paint, kind === 1 ? -6 : 1, kind === 1 ? -47 : -46, 29, 18, shade(tint, .95));
  }
  if (worked) {
    if (kind !== 0) paint([-23, -56, -18, -56, -18, -52, -22, -52], 0xc9ab79);
    paint([-5, -22, 1, -19, -5, -13], 0xd4b88b);
    paint([-21, 3, -15, 0, -10, 3, -15, 6], 0xc0a16e);
    paint([12, 7, 17, 4, 24, 7, 18, 9], 0xad915e);
  }
}
